// Types and words for disciplinary incidents and the suggestions on the
// Disciplinary page (db/migrations/4400 to 4403, docs/DISCIPLINARY-INCIDENTS.md).
// Plain .ts so web/test can import it.
//
// Nothing here decides anything. The step suggested, whether it applies to the
// driver's agreement, and every refusal come from Postgres as sentences and are
// shown as they arrive.
import type { ChipTone } from '../components/interviews/Chip'

export type Offence = {
  key: string; offence: string; kind: 'serious' | 'progressive'; code_ref: string; clause: string
  in_code_table: boolean; note: string | null; ladder: string[]; detected: boolean
}

export type IncidentOptions = {
  ok: boolean
  reporter: string | null
  offences: Offence[]
  drivers: { id: string; name: string | null; status: string; vehicle_id: string | null; plate: string | null }[]
  vehicles: { id: string; label: string | null; plate: string | null }[]
  actions: { value: string; label: string }[]
}

export type LadderStep = {
  offence: string; offence_label?: string; kind?: string; applies: boolean; terms?: string; basis?: string
  step?: string; step_label?: string; needs_hearing?: boolean; explanation: string
  prior?: { sanction: string; label: string; issued_at: string; valid_until: string | null }[]
}

export type Suggestion = {
  applies: boolean; step: string | null; step_label: string | null; needs_hearing: boolean
  primary_offence: string | null; explanation: string | null
  offences: LadderStep[] | null
  record_in_force: { sanction: string; label: string; what: string; issued_at: string; valid_until: string | null }[] | null
}

export type CaseEvent = { type: string; detail: string; at: string; by: string | null }

export type IncidentCase = {
  id: string
  status: 'notice_issued' | 'response_received' | 'hearing_referred' | 'decided' | 'withdrawn'
  proposed_sanction: string; proposed_label: string
  notice_on: string | null; notice_method: string | null; response_due: string | null; response_overdue: boolean
  response: string | null; no_response: boolean | null
  outcome: string | null; outcome_label: string | null; outcome_reasons: string | null; decided_at: string | null; decided_by: string | null
  recommendation: { id: string; ref: string; status: string } | null
  events: CaseEvent[] | null
}

export type EvidenceFile = { path: string; filename: string; mime_type: string; size_bytes: number }

export type Incident = {
  id: string; ref: string; driver_id: string; driver: string | null; vehicle: string | null
  occurred_on: string; place: string
  offences: { key: string; offence: string; code_ref: string; kind: string }[] | null
  description: string; witnesses: string; immediate_action: string; requested_action: string
  evidence: EvidenceFile[]
  reported_by: string | null; reported_at: string
  status: 'reported' | 'released' | 'closed'
  released_by: string | null; released_at: string | null
  closed_by: string | null; closed_at: string | null; closed_reason: string | null
  suggestion: Suggestion | null
  notice_text: string | null
  can_act: boolean
  case: IncidentCase | null
}

export type AutomatedSuggestion = {
  id: string; category: string; driver_id: string; driver: string | null; what: string; clause: string | null
  period_start: string; period_end: string; occurrences: number | null
  allegation: string; evidence: string | null
  step: string; step_label: string; needs_hearing: boolean
  raised_at: string; page: string; can_act: boolean; releasable: boolean
}

export type SuggestionsPage = {
  ok: boolean
  incidents: Incident[]
  automated: AutomatedSuggestion[]
  gate: { drivers: number; on_code: number }
}

export const NOTICE_METHODS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'letter', label: 'Letter' },
  { value: 'in_person_signed', label: 'In person, and he signed for it' },
] as const

export const REQUESTED_LABEL: Record<string, string> = {
  record: 'Record it',
  warning: 'Asked for a warning',
  hearing: 'Asked for a hearing',
  suspend_and_hearing: 'Asked for suspension and a hearing',
}

export function stepTone(step: string | null | undefined): ChipTone {
  if (step === 'termination') return 'red'
  if (step === 'final_written_warning' || step === 'suspension') return 'amber'
  if (step === 'no_action' || step === 'withdrawn') return 'green'
  return 'neutral'
}

/** Where an incident stands, in the words a manager would use. */
export function incidentStage(i: Pick<Incident, 'status' | 'case' | 'driver'>): { text: string; tone: ChipTone } {
  if (i.status === 'reported') return { text: 'Waiting for the manager to release or close', tone: 'amber' }
  if (i.status === 'closed') return { text: 'Closed without a step', tone: 'neutral' }
  const c = i.case
  if (!c) return { text: 'Released', tone: 'neutral' }
  switch (c.status) {
    case 'notice_issued':
      return c.response_overdue
        ? { text: 'His time to respond has passed', tone: 'amber' }
        : { text: 'Waiting for his response', tone: 'amber' }
    case 'response_received':
      return { text: 'Heard him: decision to record', tone: 'amber' }
    case 'hearing_referred':
      return c.recommendation
        ? { text: `Hearing outcome ${c.recommendation.ref} filed`, tone: 'brand' }
        : { text: 'Waiting for the hearing', tone: 'brand' }
    case 'decided':
      return { text: `Decided: ${c.outcome_label ?? 'outcome recorded'}`, tone: stepTone(c.outcome) === 'neutral' ? 'neutral' : stepTone(c.outcome) }
    case 'withdrawn':
      return { text: 'Withdrawn', tone: 'neutral' }
  }
}

/** Evidence files are images, video or documents; the label says which without opening it. */
export function fileKind(mime: string): string {
  if (mime.startsWith('image/')) return 'Photo'
  if (mime.startsWith('video/')) return 'Video'
  if (mime.startsWith('audio/')) return 'Audio'
  if (mime === 'application/pdf') return 'PDF'
  return 'File'
}

export const EVIDENCE_ACCEPT = 'image/*,video/*,audio/*,application/pdf'
const MAX_EVIDENCE = 25 * 1024 * 1024

export function checkEvidence(file: File): string | null {
  const t = (file.type || '').toLowerCase()
  if (!(t.startsWith('image/') || t.startsWith('video/') || t.startsWith('audio/') || t === 'application/pdf')) {
    return `${file.name}: choose a photo, a video, a voice note or a PDF.`
  }
  if (file.size > MAX_EVIDENCE) return `${file.name} is over 25 MB. Send a shorter clip or a smaller photo.`
  return null
}
